/**
 * The bag button's behavior half (studio-shop D4): the bag's count, read from
 * the bag kept in this browser (every tab and piece sees the same bag), and
 * Open: the page's bag drawer when the page places one, else the bag page.
 * Draws nothing.
 */
import { useMemo } from 'react';

import { useShopCatalog } from '../../api/shop';
import { usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { useBag } from '../../hooks/useBag';
import { useShopPage } from '../../hooks/useShopPage';
import { usePageMover } from '../../pageMover';
import { contract, type ShopBagButtonActions, type ShopBagButtonEvents, type ShopBagButtonState } from './contract';

export type ShopBagButtonRuntime = PieceRuntime<typeof contract, ShopBagButtonState, ShopBagButtonActions>;

export function useShopBagButton(input: PieceInput<ShopBagButtonEvents> = {}): ShopBagButtonRuntime {
  const options = usePieceOptions(contract, input.options);
  const move = usePageMover();
  const { page } = useShopPage();
  const { count } = useBag();
  const catalog = useShopCatalog();
  // A bag still holding items stays reachable, so they can be seen and removed.
  const shown = count > 0 || (catalog.data?.products.length ?? 0) > 0;

  const actions = useMemo<ShopBagButtonActions>(
    () => ({ open: () => (page.hasDrawer() ? page.openBag() : move('/shop/bag')) }),
    [page, move],
  );
  const state = useMemo<ShopBagButtonState>(
    () => ({ count, label: count === 0 ? 'Bag, empty' : `Bag, ${count} ${count === 1 ? 'item' : 'items'}`, shown }),
    [count, shown],
  );
  return { state, actions, options };
}
